import { useQuery } from '@tanstack/react-query'
import moment from 'moment/moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { twMerge as twm } from 'tailwind-merge'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { Availability } from '../../models/Availability'
import { Booking, MAX_HOURLY_DURATION } from '../../models/Booking'
import { Lesson } from '../../models/Lesson'
import { Option } from '../../models/Option'
import { RecordFactory } from '../../models/RecordFactory'
import { SHORT_DOW_MONTH_DAY_FORMAT, TIME_FORMAT } from '../../utils/dates'
import Button from '../app/design/Button'
import Divider from '../app/design/Divider'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { GuardrailModal } from '../common/modals/GuardrailModal'
import { DatePicker } from '../common/fields/DatePicker'
import { Select } from '../common/fields/Select'
import { timeHourOptionsV2, timeMinuteIntervalFiveOptionsV2 } from '../settings/Options'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { applyPackage } from './BookingWorkflow'

function postProcessAvailabilities(booking: Booking, availabilities: Availability[]) {
    const forSchedule = !!booking.forSchedule
    const forClass = booking.item?.type === 'class'
    const forPackage = !!booking.package
    const forService = booking.item?.type === 'service' || !booking.item
    const forCustomSchedule = forSchedule && !booking.schedule?.interval
    const forReservationService = booking.item?.type === 'reservation'

    let customDates: moment.Moment[] | undefined
    let customByDate: Record<string, moment.Moment | Lesson> | undefined

    if (forCustomSchedule) {
        customDates = booking.schedule?.dateValues ?? []
        customByDate = Object.fromEntries(customDates.map((date) => [date.format(), date]))
    }
    else if (forClass && !booking.id && (booking.item?.dropin || booking.lessons?.length)) {
        customDates = []
        customByDate = {}
        for (const lesson of booking.lessons ?? []) {
            if (lesson.date) {
                customDates.push(lesson.date)
                customByDate[lesson.date.format()] = lesson
            }
        }
    }
    else if (!!booking.package && !booking.id) {
        customDates = booking.dates.slice()
        customByDate = Object.fromEntries(customDates.map((date) => [date.format(), date]))
    }

    const availabilityData = filterAvailabilitiesForSchedule(
        availabilities,
        booking,
        forCustomSchedule,
        forClass
    )

    const { availabilityDays, availabilityByDate } = mapAvailabilitiesByDate(
        availabilityData,
        forClass,
        customByDate,
        customDates
    )

    return {
        booking,
        forSchedule,
        forClass,
        forPackage,
        forService,
        forCustomSchedule,
        forReservationService,
        customDates,
        customByDate,
        availabilityData,
        availabilityDays,
        availabilityByDate,
    }
}

function filterAvailabilitiesForSchedule(
    availabilities: Availability[],
    record: Booking,
    forCustomSchedule: boolean,
    forClass: boolean
) {
    if (record.forSchedule && !forCustomSchedule && !forClass) {
        availabilities = availabilities.filter((availability) => {
            let dow = availability.start?.weekday() ?? 0
            dow = dow === 1 ? Math.pow(2, 6) : Math.pow(2, dow - 2)
            return ((record.schedule?.dow ?? 0) & dow) !== 0
        })
    }
    return availabilities
}

function mapAvailabilitiesByDate(
    availabilities: Availability[],
    forClass: boolean,
    customByDate: Record<string, moment.Moment | Lesson> | undefined,
    customDates: moment.Moment[] | undefined
) {
    const l: moment.Moment[] = []
    const d: Record<string, Availability[]> = {}
    const le: Record<string, moment.Moment> = {}
    const sortedAvailabilities = availabilities
        .slice()
        .sort((a, b) => (a.start ?? moment()).diff(b.start ?? moment()))

    for (const availability of sortedAvailabilities) {
        if (!availability.start) continue

        const date = availability.start.clone().startOf('day')
        let dates = d[date.format()]

        if (!dates) {
            l.push(date)
            dates = []
            d[date.format()] = dates
        }
        dates.push(availability)

        if (forClass && availability.lesson && availability.available) {
            le[availability.start.format()] = availability.start
        }
    }

    const availabilityDays = l
    const availabilityByDate = d

    if (forClass && !!customDates && !!customByDate && Object.keys(customByDate).length) {
        const dates = { ...customByDate }

        for (const date in dates) {
            if (!le[date]) {
                delete customByDate[date]
                customDates.splice(
                    customDates.findIndex((d) => d.format() === date),
                    1
                )
            }
        }
    }

    return { availabilityDays, availabilityByDate }
}

type GuardrailType =
    | 'doubleBookSelect'
    | 'doubleBook'
    | 'waitlist'
    | 'dropinNotAllowed'
    | 'reservationDuration'

type GuardrailData = {
    type: GuardrailType | undefined
    availability: Availability | undefined
}

function guardrailTitle(type: GuardrailType): string {
    const titles: Record<GuardrailType, string> = {
        doubleBookSelect: 'Double book',
        doubleBook: 'Double book',
        waitlist: 'Wait list',
        dropinNotAllowed: 'Drop-ins not allowed',
        reservationDuration: 'Reservation duration',
    }
    return titles[type]
}

function guardrailButtonText(type: GuardrailType): string {
    const buttonTexts: Record<GuardrailType, string> = {
        doubleBookSelect: 'Double book',
        doubleBook: 'Double book',
        waitlist: 'Add to wait list',
        dropinNotAllowed: 'OK',
        reservationDuration: 'OK',
    }
    return buttonTexts[type]
}

function guardrailMessage(type: GuardrailType): string {
    const messages: Record<GuardrailType, string> = {
        doubleBookSelect: 'Would you like to double book this time?',
        doubleBook: 'Would you like to double book this time?',
        waitlist:
            'This class is currently full. You can add this client to the Wait \
            List for this class. If space becomes available up to 12 hours before \
            the class, the client will be automatically booked into the class.',
        dropinNotAllowed:
            'This class does not support drop-ins. You will be registered for the entire class.',
        reservationDuration: 'Please select # of days for this reservation.',
    }
    return messages[type]
}

type Props = {
    isOpen: boolean
    booking: Booking
    onClose: () => void
    onDone: (booking: Booking) => void
}

export const AvailabilityModal = ({ isOpen, booking: _booking, onClose, onDone }: Props) => {
    const { logAndDisplayError } = useErrorHandler()

    const [booking, setBooking] = useState(_booking)

    const { data, isLoading } = useQuery({
        queryKey: [
            'availability',
            booking.id,
            booking.date?.toISOString(),
            booking.item?.id,
            booking.location,
            booking.duration,
            booking.vendor?.id,
            booking.client?.id,
        ],
        queryFn: async () => {
            const { date, item, location, duration, vendor, client } = booking

            let result

            if (item?.type === 'class') {
                result = await RecordFactory.getInstance().searchClassAvailability(
                    date,
                    undefined,
                    true,
                    item,
                    location,
                    vendor,
                    false
                )
            }
            else if (item?.type === 'reservation' || (duration ?? 0) > MAX_HOURLY_DURATION) {
                result = await RecordFactory.getInstance().searchReservationAvailability(
                    booking,
                    date,
                    undefined,
                    true,
                    item,
                    location,
                    duration,
                    false,
                    undefined
                )
            }
            else {
                result = await RecordFactory.getInstance().searchAvailability(
                    booking,
                    date,
                    undefined,
                    item,
                    location,
                    duration,
                    client,
                    false,
                    undefined
                )
            }

            if (!result.ok) throw result.error

            const availabilities =
                result.value.length > 0 && result.value[0].hasValueForKey('availability')
                    ? result.value[0].valueForRecordListKey<Availability>(
                          'availability',
                          Availability
                      )
                    : []

            return postProcessAvailabilities(booking, availabilities ?? [])
        },
        onError: (error) => {
            logAndDisplayError(error)
        },
    })

    const [custom, setCustom] = useState({
        dates: data?.customDates,
        byDate: data?.customByDate,
    })

    useEffect(() => {
        setCustom({
            dates: data?.customDates,
            byDate: data?.customByDate,
        })
    }, [data?.customDates, data?.customByDate])

    const [nextEnabled, setNextEnabled] = useState(false)
    const [selectedDatesTitle, setSelectedDatesTitle] = useState('Dates')
    const [showSelectedDates, setShowSelectedDates] = useState(false)

    const [guardrail, setGuardrail] = useState<GuardrailData>({
        type: undefined,
        availability: undefined,
    })

    const forReserve = useMemo(() => {
        return data?.forReservationService || (booking.duration ?? 0) > MAX_HOURLY_DURATION
    }, [data?.forReservationService, booking.duration])

    const dateSelectionDisabled = useMemo(() => {
        return data?.forReservationService
            ? !!booking.item?.startDate
            : data?.forClass && !booking.item?.dropin
    }, [data?.forClass, data?.forReservationService, booking.item])

    const { showDuration, durationOptions } = useMemo(() => {
        const reservationWithFlexibleDuration =
            data?.forReservationService && !booking.item?.duration
        if (!data?.forClass && (!data?.forReservationService || reservationWithFlexibleDuration)) {
            if (data?.forReservationService) {
                const durationStrings = Booking.durationStrings(false, true)
                durationStrings.unshift(
                    new Option(undefined, { id: '', label: 'Select # of days' })
                )
                return {
                    showDuration: true,
                    durationOptions: durationStrings.map((option) => ({
                        id: option.id ?? '',
                        title: option.label ?? '',
                    })),
                }
            }
            return {
                showDuration: true,
                durationOptions: Booking.durationStrings(
                    true,
                    !booking.item && !!booking.vendor?.industry?.reservations
                ).map((option) => ({
                    id: option.id ?? '',
                    title: option.label ?? '',
                })),
            }
        }
        return { showDuration: false, durationOptions: [] }
    }, [data?.forClass, data?.forReservationService, booking.item, booking.vendor])

    const timeHourOptions = useMemo(
        () =>
            timeHourOptionsV2.map((option) => ({
                id: option.id ?? '',
                title: option.label ?? '',
            })),
        []
    )

    const timeMinuteOptions = useMemo(
        () =>
            timeMinuteIntervalFiveOptionsV2.map((option) => ({
                id: option.id ?? '',
                title: option.label ?? '',
            })),
        []
    )

    const [selectedHourOption, setSelectedHourOption] = useState<Option | undefined>(
        timeHourOptionsV2.find(
            (o) => o.id === (booking.date ?? moment()).hour().toString().padStart(2, '0')
        )
    )

    const [selectedMinuteOption, setSelectedMinuteOption] = useState<Option | undefined>(
        timeMinuteIntervalFiveOptionsV2.find(
            (o) => o.id === (Math.floor((booking.date ?? moment()).minute() / 5) * 5).toString()
        )
    )

    const title = useMemo(() => {
        if (data?.forClass) return 'Class schedule'
        if (booking.forSchedule) return 'Dates & times'
        return 'Date & time'
    }, [data?.forClass, booking.forSchedule])

    const didChangeSchedule = useCallback(() => {
        if (!custom.dates) return
        const numberOfDates = custom.dates.length
        setNextEnabled(
            numberOfDates >=
                (data?.forCustomSchedule ? 2 : data?.forClass || custom.dates.length > 0 ? 1 : 0)
        )
        setSelectedDatesTitle(numberOfDates ? `Dates (${numberOfDates})` : 'Dates')
    }, [data?.forClass, data?.forCustomSchedule, custom.dates])

    useEffect(() => {
        if (custom.dates !== undefined) {
            didChangeSchedule()
        }
        else {
            setNextEnabled(
                (!data?.forClass || data.availabilityData.length > 0) &&
                    (!data?.forReservationService || !!booking.item?.duration || !!booking.duration)
            )
        }
    }, [
        data?.forClass,
        data?.forReservationService,
        data?.availabilityData,
        booking.duration,
        booking.item,
        custom.dates,
        didChangeSchedule,
    ])

    const selectTimeSlot = useCallback(
        (availability: Availability) => {
            if (!availability.start) return

            const date = availability.start
            const lesson = data?.forClass ? availability.lesson : undefined

            setCustom((prev) => ({
                ...prev,
                dates: [
                    ...(prev.dates ?? []).filter((d) => d.format() !== date.format()),
                    date,
                ].sort((a, b) => a.diff(b)),
                byDate: {
                    ...prev.byDate,
                    [date.format()]: !!lesson ? lesson : date,
                },
            }))
        },
        [data?.forClass]
    )

    const selectAvailability = useCallback(
        (availability: Availability) => {
            if (availability.available) {
                selectTimeSlot(availability)
            }
            else {
                setGuardrail({ type: 'doubleBookSelect', availability })
            }
        },
        [selectTimeSlot]
    )

    const deselectAvailability = useCallback((availability: Availability) => {
        if (!availability.start) return

        const date = availability.start

        setCustom((prev) => ({
            ...prev,
            dates: (prev.dates ?? []).filter((d) => d.format() !== date.format()),
            byDate: Object.fromEntries(
                Object.entries(prev.byDate ?? {}).filter(([key]) => key !== date.format())
            ),
        }))
    }, [])

    const selectNext = useCallback(async () => {
        if (data?.forCustomSchedule && custom.dates?.length) {
            booking.dates = custom.dates
            booking.date = custom.dates[0]

            if (!!booking.schedule && custom.dates.length > 1) {
                booking.schedule.dateValues = custom.dates
                booking.schedule.instances = custom.dates.length
                booking.schedule.date = booking.date
            }
            else {
                booking.schedule = undefined
            }
        }
        else if (custom.dates?.length) {
            booking.dates = custom.dates
            booking.date =
                custom.dates.length === 0 && data?.forPackage ? booking.date : custom.dates[0]

            if (data?.forClass) {
                const l: Lesson[] = []
                for (const date of custom.dates) {
                    if (!!custom.byDate) l.push(custom.byDate[date.format()] as Lesson)
                }
                booking.lessons = l
            }
        }
        else if (!!booking.schedule) {
            booking.schedule.date = booking.date
            if (booking.schedule.weekDays.length < 2) {
                booking.schedule.setWeekDaysFromDate(booking.date)
            }
        }

        if (
            !booking.package &&
            data?.forReservationService &&
            !booking.item?.duration &&
            booking.item?.rate
        ) {
            const qty = (booking.duration ?? 0) / 1440
            const total = booking.item.rate * qty
            const line = booking.lines.find((l) => l.item && l.item.id === booking.item?.id)
            if (line) {
                line.total = total
                line.quantity = qty
                line.rate = line.total / line.quantity
            }

            if (booking.lines.length) {
                let total = 0
                for (const line of booking.lines) {
                    if (line.total) {
                        total += line.total
                    }
                    else if (line.rate) {
                        total += line.rate * (line.quantity ? line.quantity : 1)
                    }
                }
                booking.rate = total - (booking.deposit ?? 0)
            }
        }

        if (data?.forReservationService && !!booking.date) {
            const endDate = booking.date.clone().startOf('day')
            endDate.add(Math.floor((booking.duration ?? 0) / 1440), 'days')

            let checkOutHour = endDate.hour()
            let checkOutMinute = endDate.minute()

            const checkOutTime = booking.item?.checkOutTime
            if (checkOutTime !== undefined) {
                checkOutHour = Math.floor(checkOutTime)
                checkOutMinute = checkOutTime % 1 > 0 ? 30 : 0
            }

            booking.endDate = endDate.hour(checkOutHour).minute(checkOutMinute)

            let checkInHour = booking.date.hour()
            let checkInMinute = booking.date.minute()

            const checkInTime = booking.item?.checkInTime
            if (checkInTime !== undefined) {
                checkInHour = Math.floor(checkInTime)
                checkInMinute = checkInTime % 1 > 0 ? 30 : 0
            }

            booking.date = booking.date.clone().hour(checkInHour).minute(checkInMinute)
        }

        if (
            booking.package &&
            booking.package.capacityType === 'duration' &&
            (booking.duration ?? 0) > (data?.booking.duration ?? 0)
        ) {
            const left = (await booking.package.available()) ?? 0
            if (left < (booking.duration ?? 0)) {
                await applyPackage(booking, undefined)
            }
        }

        onDone(booking)
        onClose()
    }, [
        booking.date,
        data?.forCustomSchedule,
        data?.forPackage,
        data?.forClass,
        data?.forReservationService,
        custom.dates,
        custom.byDate,
        onDone,
        onClose,
    ])

    const next = useCallback(() => {
        if (data?.forClass && custom.dates === undefined) {
            booking.date = data.availabilityData[0].start
            booking.dropin = false
            booking.lesson = undefined
            booking.dates = []
            booking.lessons = []
        }
        selectNext()
    }, [data?.forClass, booking, custom.dates, data?.availabilityData, selectNext])

    const selectDateAndNext = useCallback(
        (availability: Availability) => {
            if (custom.dates !== undefined) {
                selectTimeSlot(availability)
                selectNext()
            }
            else {
                if (data?.forClass) {
                    const firstAvailability = data.availabilityDays.length
                        ? data.availabilityByDate[data.availabilityDays[0].format()]?.[0]
                        : undefined

                    if (!(availability === firstAvailability)) {
                        setGuardrail({
                            type: 'dropinNotAllowed',
                            availability: firstAvailability,
                        })
                        return
                    }

                    booking.date = firstAvailability.start
                    booking.dropin = false
                    booking.lesson = undefined
                    booking.dates = []
                    booking.lessons = []
                    selectNext()
                }
                else {
                    booking.date = availability.start
                    selectNext()
                }
            }
        },
        [
            data?.forClass,
            booking,
            custom.dates,
            data?.availabilityDays,
            data?.availabilityByDate,
            selectTimeSlot,
            selectNext,
        ]
    )

    const bookAvailability = useCallback(
        (availability: Availability) => {
            if (
                data?.forClass &&
                !!booking.item?.dropin &&
                availability.actual !== undefined &&
                availability.capacity !== undefined &&
                availability.actual >= availability.capacity
            ) {
                setGuardrail({ type: 'waitlist', availability })
            }
            else if (!availability.available) {
                setGuardrail({ type: 'doubleBook', availability })
            }
            else if (
                data?.forReservationService &&
                !booking.item?.duration &&
                (booking.duration ?? 0) <= 0
            ) {
                setGuardrail({ type: 'reservationDuration', availability })
            }
            else {
                selectDateAndNext(availability)
            }
        },
        [
            data?.forClass,
            data?.forReservationService,
            booking.item,
            booking.duration,
            selectDateAndNext,
        ]
    )

    const availabilityTitle = (availability: Availability) => {
        return !booking.duration && !data?.forReservationService
            ? `Book ${moment(availability.start).format(TIME_FORMAT)}`
            : forReserve
              ? `Reserve ${moment(availability.start).format(TIME_FORMAT)}`
              : `${moment(availability.start).format(TIME_FORMAT)} - ${moment(availability.end).format(TIME_FORMAT)}`
    }

    const availabilityColorClass = (date: moment.Moment, availability: Availability) => {
        if (
            booking.id &&
            booking.date?.isSameOrAfter(availability.start) &&
            booking.date?.isBefore(availability.end)
        ) {
            if (
                data?.forClass &&
                !booking.item?.dropin &&
                (!data.availabilityDays[0].isSame(date) ||
                    data.availabilityByDate[date.format()][0] !== availability)
            ) {
                return 'text-gray-400'
            }
            else {
                return 'text-primary-700'
            }
        }
        else if (!availability.available) {
            return 'text-danger-400'
        }

        return 'text-success-400'
    }

    const isAvailabilitySelected = (availability: Availability) => {
        return custom.byDate !== undefined
            ? custom.byDate[availability.start?.format() ?? '']
            : booking.date?.isSame(availability.start)
    }

    const onSelectAvailability = (availability: Availability) => {
        if (custom.byDate !== undefined) {
            isAvailabilitySelected(availability)
                ? deselectAvailability(availability)
                : selectAvailability(availability)
        }
        else {
            bookAvailability(availability)
        }
    }

    const setDate = useCallback(
        (selectedDate: Date) => {
            const newDate = moment(selectedDate)
            const hour = selectedHourOption?.id ? parseInt(selectedHourOption.id) : newDate.hour()
            const minute = selectedMinuteOption?.id
                ? parseInt(selectedMinuteOption.id)
                : Math.floor(newDate.minute() / 5) * 5
            const date = newDate.hour(hour).minute(minute)

            if (booking.date?.isSame(date)) return

            setBooking(booking.clone({ date }))
        },
        [booking, selectedHourOption, selectedMinuteOption]
    )

    const onGuardrailAction = (type: GuardrailType, data: any) => {
        switch (type) {
            case 'doubleBookSelect':
                selectTimeSlot(data)
                break
            case 'doubleBook':
            case 'waitlist':
                selectDateAndNext(data)
                break
            case 'dropinNotAllowed':
                booking.date = data.start
                booking.dropin = false
                booking.lesson = undefined
                booking.dates = []
                booking.lessons = []
                selectNext()
                break
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            title={showSelectedDates ? selectedDatesTitle : title}
            onClose={onClose}
            onNavigateBack={showSelectedDates ? () => setShowSelectedDates(false) : undefined}
            footer={
                !showSelectedDates && (
                    <div className="flex gap-4">
                        {!!custom.dates && (
                            <Button
                                onClick={() => setShowSelectedDates(true)}
                                disabled={custom.dates?.length === 0}
                            >
                                {selectedDatesTitle}
                            </Button>
                        )}

                        <Button
                            intent="primary"
                            onClick={next}
                            disabled={isLoading || !nextEnabled}
                            isLoading={isLoading}
                        >
                            Done
                        </Button>
                    </div>
                )
            }
        >
            {!showSelectedDates && (
                <div className="flex flex-col gap-8">
                    {!dateSelectionDisabled && (
                        <WorkflowPage.IconSection title="Start date" icon="calendar.large">
                            <DatePicker
                                size="medium"
                                selectedDateString={booking.date?.toISOString()}
                                minDateString={
                                    !!booking.date
                                        ? moment.min(moment(), booking.date).toISOString()
                                        : moment().toISOString()
                                }
                                onSelectDate={setDate}
                            />
                        </WorkflowPage.IconSection>
                    )}

                    {showDuration && (
                        <WorkflowPage.IconSection title="Duration" icon="stopwatch.large">
                            <Select
                                size="medium"
                                selectedOptionID={
                                    booking.duration ? booking.duration.toString() : ''
                                }
                                options={durationOptions}
                                onSelectOption={(option) => {
                                    setBooking(booking.clone({ duration: parseInt(option.id) }))
                                }}
                                isDisabled={isLoading}
                            />
                        </WorkflowPage.IconSection>
                    )}

                    {!dateSelectionDisabled && (
                        <WorkflowPage.IconSection title="Time" icon="clock.large">
                            <div className="items-grow flex gap-2">
                                <Select
                                    size="medium"
                                    selectedOptionID={selectedHourOption?.id ?? ''}
                                    options={timeHourOptions}
                                    onSelectOption={(option) => {
                                        setSelectedHourOption(new Option(option))
                                        booking.date = (booking.date ?? moment()).hour(
                                            parseInt(option.id)
                                        )
                                    }}
                                    isDisabled={isLoading || dateSelectionDisabled}
                                />
                                <Select
                                    size="medium"
                                    selectedOptionID={selectedMinuteOption?.id ?? ''}
                                    options={timeMinuteOptions}
                                    onSelectOption={(option) => {
                                        setSelectedMinuteOption(new Option(option))
                                        booking.date = (booking.date ?? moment()).minute(
                                            parseInt(option.id)
                                        )
                                    }}
                                    isDisabled={isLoading || dateSelectionDisabled}
                                />
                            </div>
                        </WorkflowPage.IconSection>
                    )}

                    {isLoading && (
                        <Skeleton
                            className="h-18"
                            count={3}
                            borderRadius={8}
                            baseColor="#f6f6f6"
                            highlightColor="#ffffff"
                        />
                    )}

                    {!isLoading && data?.availabilityData.length === 0 && (
                        <div className="pt-4 pl-1">
                            {data?.forClass
                                ? 'That class is not available as of the date selected.'
                                : data?.forSchedule
                                  ? 'There are no available times. Please try changing your schedule.'
                                  : 'There are no available times. Please try another date.'}
                        </div>
                    )}

                    {!isLoading &&
                        data?.availabilityDays.map((date) => (
                            <div key={date.format()}>
                                <Label>{date.format('dddd, MMMM D')}</Label>
                                <Divider />
                                <div className="flex flex-col gap-4">
                                    {data.availabilityByDate[date.format()].map((availability) => (
                                        <WorkflowPage.IconSection
                                            key={availability.start?.format()}
                                            icon={<></>}
                                            title={availabilityTitle(availability)}
                                            thirdLine={
                                                <span
                                                    className={availabilityColorClass(
                                                        date,
                                                        availability
                                                    )}
                                                >
                                                    {availability.available
                                                        ? 'Available'
                                                        : 'Unavailable'}
                                                </span>
                                            }
                                            secondaryView={
                                                <Button
                                                    size="small"
                                                    intent="tertiaryLink"
                                                    className="mr-2 translate-x-3 hover:opacity-100"
                                                    onClick={() =>
                                                        onSelectAvailability(availability)
                                                    }
                                                >
                                                    {isAvailabilitySelected(availability)
                                                        ? 'Selected'
                                                        : 'Select'}
                                                </Button>
                                            }
                                            className={twm(
                                                'p-4',
                                                isAvailabilitySelected(availability) &&
                                                    'border-secondary-400 rounded-lg border-2'
                                            )}
                                            hasHoverEffect
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                </div>
            )}

            {showSelectedDates && (
                <div className="flex flex-col gap-4">
                    {custom.dates?.length === 0 && (
                        <div className="pt-4 pl-1">
                            {(data?.availabilityData ?? []).length > 0
                                ? data?.forCustomSchedule
                                    ? 'Please select at least 2 dates.'
                                    : data?.forClass
                                      ? 'Please select a class.'
                                      : 'Please select a date.'
                                : 'There are no available times. Please try another date.'}
                        </div>
                    )}
                    {custom.dates?.map((date) => (
                        <WorkflowPage.IconSection
                            key={date.format()}
                            icon={<></>}
                            title={date.format(SHORT_DOW_MONTH_DAY_FORMAT)}
                            description={date.format(TIME_FORMAT)}
                        />
                    ))}
                </div>
            )}

            {!!guardrail.availability && (
                <>
                    {(
                        [
                            'doubleBookSelect',
                            'doubleBook',
                            'waitlist',
                            'dropinNotAllowed',
                            'reservationDuration',
                        ] as const
                    ).map(
                        (type) =>
                            guardrail.type === type && (
                                <GuardrailModal
                                    key={type}
                                    title={guardrailTitle(type)}
                                    buttonText={guardrailButtonText(type)}
                                    onClose={() =>
                                        setGuardrail({
                                            type: undefined,
                                            availability: undefined,
                                        })
                                    }
                                    onAction={() => {
                                        onGuardrailAction(type, guardrail.availability)
                                        setGuardrail({
                                            type: undefined,
                                            availability: undefined,
                                        })
                                    }}
                                >
                                    <Paragraph>{guardrailMessage(type)}</Paragraph>
                                </GuardrailModal>
                            )
                    )}
                </>
            )}
        </Modal>
    )
}
